import { SWRResponse } from 'swr';
import { ReqOptions } from './use-auth-req-options';
import { useFetch } from '~/common/hooks/use-fetch';
import { EnabledFeatureControllerApi, EnabledFeaturesDtoEnabledFeaturesEnum } from '~/common/api';

type EnabledFeatureMap = {
  isDashboardEnabled: boolean;
  isAnalyticsDataDownloadEnabled: boolean;
  isTableauDashboardEnabled: boolean;
  isComparisonSheetSelectedConditionSaveEnabled: boolean;
  isActualItemDetailViewEnabled: boolean;
  isBulkPlanUpdateRequestEnabled: boolean;
  isComparisonSheetHighlightEnabled: boolean;
  isWorkforcePlanningEnabled: boolean;
  isNonFinancialActualCSVFormat: boolean;
  isAllocationByDimensions: boolean;
  isViewPermissionEnabled: boolean;
  isAsyncAllocationExecutionEnabled: boolean;
  isFinancialSheetHistoryAndDepartmentEnabled: boolean;
  isApiIntegrationEnabled: boolean;
  isForeignCurrencyEnabled: boolean;
  isFinancialSimulatorEnabled: boolean;
  isTmpAllocationFeatureForIneEnabled: boolean;
  isUnitPriceDataImportActualEnabled: boolean;
  isCashFlowEnabled: boolean;
  isAllocationRemakeEnabled: boolean;
  isNewDashboardEnabled: boolean;
  isCustomPlanFormatEnabled: boolean;
  isAllocationOldDeactivationEnabled: boolean;
  isCashFlowPolarsEnabled: boolean;
  isAiAssistantEnabled: boolean;
  isAiAssistantForCorpPlanningEnabled: boolean;
  isAiAssistantForEditorEnabled: boolean;
  isAiAssistantForViewerEnabled: boolean;
  isNewWorkforcePlanningSimulationEnabled: boolean;
  isOldWorkforcePlanningSimulationEnabled: boolean;
  isCategoryAxisSelectionEnabled: boolean;
  isReportEnhanceEnabled: boolean;
  isAiChatMaintenanceEnabled: boolean;
  isAiChatSpeedModeThinkingDisabledEnabled: boolean;
  isEmailNotificationPerFeatureEnabled: boolean;
};

const convertToFeatureMap = (
  enabledFeatures?: EnabledFeaturesDtoEnabledFeaturesEnum[],
): EnabledFeatureMap | undefined => {
  if (!enabledFeatures) return;

  const enabledFeatureMap: Map<EnabledFeaturesDtoEnabledFeaturesEnum, boolean> =
    enabledFeatures.reduce((acc, value) => acc.set(value, true), new Map());

  return {
    isDashboardEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.DevTableauDashboard,
    ),
    isAnalyticsDataDownloadEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AnalyticsDataDownload,
    ),
    isTableauDashboardEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.DevTableauDashboard,
    ),
    isComparisonSheetSelectedConditionSaveEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ComparisonSheetSelectedConditionSave,
    ),
    isActualItemDetailViewEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ActualItemDetailView,
    ),
    isAllocationByDimensions: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AllocationByDimensions,
    ),
    isBulkPlanUpdateRequestEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.BulkPlanUpdateRequest,
    ),
    isComparisonSheetHighlightEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ComparisonSheetHighlight,
    ),
    isWorkforcePlanningEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.WorkforcePlanning,
    ),
    isNonFinancialActualCSVFormat: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.NonFinancialActualCsvFormat,
    ),
    isViewPermissionEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ViewPermission,
    ),
    isAsyncAllocationExecutionEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AsyncAllocationExecution,
    ),
    isFinancialSheetHistoryAndDepartmentEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.FinancialSheetHistoryAndDepartment,
    ),
    isApiIntegrationEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ApiIntegration,
    ),
    isForeignCurrencyEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ForeignCurrency,
    ),
    isFinancialSimulatorEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.FinancialSimulator,
    ),
    isTmpAllocationFeatureForIneEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.TmpAllocationFeatureForIne,
    ),
    isUnitPriceDataImportActualEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.UnitPriceDataImportActual,
    ),
    isCashFlowEnabled: !!enabledFeatureMap.get(EnabledFeaturesDtoEnabledFeaturesEnum.CashFlow),
    isAllocationRemakeEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AllocationRemake,
    ),
    isNewDashboardEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.NewDashboard,
    ),
    isCustomPlanFormatEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.CustomPlanFormat,
    ),
    isAllocationOldDeactivationEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AllocationOldDeactivation,
    ),
    isCashFlowPolarsEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.CashFlowPolars,
    ),
    isAiAssistantEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AiAssistant,
    ),
    isAiAssistantForCorpPlanningEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AiAssistantForCorpPlanning,
    ),
    isAiAssistantForEditorEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AiAssistantForEditor,
    ),
    isAiAssistantForViewerEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AiAssistantForViewer,
    ),
    isNewWorkforcePlanningSimulationEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.NewWorkforcePlanningSimulation,
    ),
    isOldWorkforcePlanningSimulationEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.OldWorkforcePlanningSimulation,
    ),
    isCategoryAxisSelectionEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.CategoryAxisSelection,
    ),
    isReportEnhanceEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.ReportEnhance,
    ),
    isAiChatMaintenanceEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AiChatMaintenance,
    ),
    isAiChatSpeedModeThinkingDisabledEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.AiChatSpeedModeThinkingDisabled,
    ),
    isEmailNotificationPerFeatureEnabled: !!enabledFeatureMap.get(
      EnabledFeaturesDtoEnabledFeaturesEnum.EmailNotificationPerFeature,
    ),
  };
};

/**
 * 有効化されているフィーチャーフラグのデータをMap形式で取得する
 */
const useEnabledFeatureMap = (): SWRResponse<EnabledFeatureMap | undefined, Error> => {
  const response = useEnabledFeatures();
  return {
    ...response,
    data: convertToFeatureMap(response.data),
    mutate: async () => {
      const result = await response.mutate();
      return convertToFeatureMap(result);
    },
  };
};

/**
 * 有効化されているフィーチャーフラグのデータを配列形式で取得する
 */
const useEnabledFeatures = (): SWRResponse<
  EnabledFeaturesDtoEnabledFeaturesEnum[] | undefined,
  Error
> => {
  return useFetch(
    FEATURES_CACHE_KEY,
    (reqOps) => fetcher(reqOps).then((response) => response.data.enabledFeatures),
    fetcherOptions,
  );
};

const FEATURES_CACHE_KEY = '/api/features' as const;
const fetcher = async (reqOps: ReqOptions) =>
  new EnabledFeatureControllerApi().listEnabledFeatures(reqOps);
const fetcherOptions = {
  dedupingInterval: 1000 * 60 * 60, // フィーチャーフラグはあまり変化しないので、1時間メモリ上のキャッシュに乗せる。リロードでリフレッシュする。
};

export { useEnabledFeatures, useEnabledFeatureMap };
export type { EnabledFeatureMap };
